import { httpClient } from './http-client';
import { ApiError } from './api.types';

export interface SunatRucData {
  ruc: string;
  razonSocial: string;
  nombreComercial?: string;
  esNuevoRus?: boolean;
  telefonos?: string[];
  tipo?: string;
  estado?: string;
  condicion?: string;
  direccion: string;
  direccionFiscal?: string;
  departamento?: string;
  provincia?: string;
  distrito?: string;
  fechaInscripcion?: string;
  sistEmsion?: string;
  sistemaEmision?: string;
  sistContabilidad?: string;
  actExterior?: string;
  actEconomicas?: string[];
  cpPago?: string[];
  sistElectronica?: string[];
  fechaEmisorFe?: string;
  emisionElectronica?: boolean;
  cpeElectronico?: string[];
  fechaPle?: string | null;
  padrones?: string[];
  fechaBaja?: string | null;
  profesion?: string;
  esAgenteRetencion?: boolean;
  esAgentePercepcion?: boolean;
  esBuenContribuyente?: boolean;
  agenteRetencionDetalle?: string;
  agentePercepcionDetalle?: string;
  buenContribuyenteDetalle?: string;
  esPrico?: boolean;
  codigoDepartamento?: string;
  codigoProvincia?: string;
  codigoDistrito?: string;
  codigoUbigeo?: string;
  ubigeo?: string;
  obligadoEmisorElectronico?: Array<{
    numeroRuc?: string;
    nombre?: string;
    estado?: string;
    condicion?: string;
    emisionElectronica?: boolean;
  }>;
}

export interface RucValidationResult {
  isValid: boolean;
  message: string;
  data?: {
    razonSocial: string;
    nombreComercial: string;
    direccionFiscal: string;
    ubigeo: string;
    actividadEconomica: string;
    codigoDepartamento: string;
    codigoProvincia: string;
    codigoDistrito: string;
    departamento: string;
    provincia: string;
    distrito: string;
  };
  rawData?: SunatRucData;
}

type ApiRucData = {
  ruc: string;
  razonSocial: string;
  nombreComercial: string;
  esNuevoRus: boolean;
  tipo: string;
  estado: string;
  condicion: string;
  direccion: string;
  departamento: string;
  provincia: string;
  distrito: string;
  ubigeoSunat: string;
  fechaInscripcion: string | null;
  sistemaEmision: string;
  sistContabilidad: string;
  fechaEmisorFe: string | null;
  emisionElectronica: boolean;
  fechaPle: string | null;
  fechaBaja: string | null;
  actEconomicas: string[];
  cpeElectronico: string[];
  padrones: string[];
  esAgenteRetencion: boolean;
  esAgentePercepcion: boolean;
  esBuenContribuyente: boolean;
  agenteRetencionDetalle: string;
  agentePercepcionDetalle: string;
  buenContribuyenteDetalle: string;
  esPrico: boolean;
};

const validateRucFormat = (ruc: string): RucValidationResult | null => {
  if (!ruc || ruc.length !== 11)
    return { isValid: false, message: 'El RUC debe tener 11 dígitos' };
  if (!/^\d+$/.test(ruc))
    return { isValid: false, message: 'El RUC solo debe contener números' };
  return null;
};

const mapRawToSunatData = (raw: ApiRucData): SunatRucData => {
  const ubigeo = raw.ubigeoSunat || '';
  return {
    ruc:                      raw.ruc,
    razonSocial:              raw.razonSocial,
    nombreComercial:          raw.nombreComercial,
    esNuevoRus:               raw.esNuevoRus,
    tipo:                     raw.tipo,
    estado:                   raw.estado,
    condicion:                raw.condicion,
    direccion:                raw.direccion,
    direccionFiscal:          raw.direccion,
    departamento:             raw.departamento,
    provincia:                raw.provincia,
    distrito:                 raw.distrito,
    fechaInscripcion:         raw.fechaInscripcion ?? undefined,
    sistemaEmision:           raw.sistemaEmision,
    sistEmsion:               raw.sistemaEmision,
    sistContabilidad:         raw.sistContabilidad,
    fechaEmisorFe:            raw.fechaEmisorFe ?? undefined,
    emisionElectronica:       raw.emisionElectronica,
    fechaPle:                 raw.fechaPle,
    fechaBaja:                raw.fechaBaja,
    actEconomicas:            raw.actEconomicas,
    cpeElectronico:           raw.cpeElectronico,
    padrones:                 raw.padrones,
    esAgenteRetencion:        raw.esAgenteRetencion,
    esAgentePercepcion:       raw.esAgentePercepcion,
    esBuenContribuyente:      raw.esBuenContribuyente,
    agenteRetencionDetalle:   raw.agenteRetencionDetalle,
    agentePercepcionDetalle:  raw.agentePercepcionDetalle,
    buenContribuyenteDetalle: raw.buenContribuyenteDetalle,
    esPrico:                  raw.esPrico,
    codigoDepartamento:       ubigeo.slice(0, 2),
    codigoProvincia:          ubigeo.slice(2, 4),
    codigoDistrito:           ubigeo.slice(4, 6),
    codigoUbigeo:             ubigeo,
    ubigeo,
  };
};

const extractActividadPrincipal = (actEconomicas?: string[]): string => {
  if (actEconomicas?.[0] && /^\s*Principal\b/i.test(actEconomicas[0]))
    return actEconomicas[0];
  return '';
};

const buildValidResult = (data: SunatRucData): RucValidationResult => ({
  isValid: true,
  message: 'RUC válido. Datos completados automáticamente desde SUNAT.',
  data: {
    razonSocial:        data.razonSocial || '',
    nombreComercial:    data.nombreComercial || '',
    direccionFiscal:    data.direccionFiscal || data.direccion || '',
    ubigeo:             data.codigoUbigeo || data.ubigeo || '',
    actividadEconomica: extractActividadPrincipal(data.actEconomicas),
    codigoDepartamento: data.codigoDepartamento || '',
    codigoProvincia:    data.codigoProvincia || '',
    codigoDistrito:     data.codigoDistrito || '',
    departamento:       data.departamento || '',
    provincia:          data.provincia || '',
    distrito:           data.distrito || '',
  },
  rawData: data,
});

export interface SunatEstadoRiesgo {
  esRiesgoso: boolean;
  mensaje?: string;
}

export const evaluarEstadoSunat = (data: Pick<SunatRucData, 'estado' | 'condicion'>): SunatEstadoRiesgo => {
  const estado = data.estado?.trim().toUpperCase();
  const condicion = data.condicion?.trim().toUpperCase();
  const estadoInvalido = !!estado && estado !== 'ACTIVO';
  const condicionInvalida = !!condicion && condicion !== 'HABIDO';

  if (!estadoInvalido && !condicionInvalida) return { esRiesgoso: false };

  const detalles = [
    estadoInvalido ? `estado "${data.estado}"` : null,
    condicionInvalida ? `condición "${data.condicion}"` : null,
  ].filter(Boolean).join(' y ');

  return {
    esRiesgoso: true,
    mensaje: `SUNAT registra ${detalles} para este RUC. El comprobante podría ser rechazado.`,
  };
};

export const sunatService = {
  validateRuc: async (ruc: string): Promise<RucValidationResult> => {
    const formatError = validateRucFormat(ruc);
    if (formatError) return formatError;

    try {
      const response = await httpClient.get<ApiRucData>(`/consulta/ruc/${ruc}`);
      const data = mapRawToSunatData(response.data);

      if (!data.razonSocial) {
        return { isValid: false, message: 'No se pudieron obtener los datos del RUC. Intenta nuevamente.' };
      }

      return buildValidResult(data);
    } catch (error) {
      if (error instanceof ApiError)
        return { isValid: false, message: error.message };
      return { isValid: false, message: 'Error al consultar el RUC. Verifica tu conexión e intenta nuevamente.' };
    }
  },
};
